import { parse, isValid, format, isAfter, isBefore, startOfDay } from 'date-fns';

import { hasInvalidCharacters } from './invalidCharacters';

export const CUSTOMER_ID_MAX_LENGTH = 100;
export const CUSTOMER_NAME_MAX_LENGTH = 200;
export const NAME_PART_MAX_LENGTH = 100;
export const DEFAULT_FIELD_MAX_LENGTH = 200;
export const CROSS_CHECK_DATE_FORMAT = 'MMM dd, yyyy';

const FIELD_TYPE_NAMES = ['text', 'numeric', 'date', 'country'];
const FIELD_RULE_NAMES = ['none', 'alphanumeric', 'ustin', 'phonenumber', 'dateofbirth'];

const US_TIN_REGEX = /^[0-9]{9}$/;
const PHONE_REGEX = /^[0-9 +\-()]{1,30}$/;
const ALPHANUMERIC_REGEX = /^[\p{L}\p{N}]+$/u;
const MIN_DATE_OF_BIRTH = new Date(1900, 0, 1);

const toName = (value, names) => (typeof value === 'number' ? names[value] : String(value || '').toLowerCase());

export const getFieldType = field => toName(field && field.fieldType, FIELD_TYPE_NAMES);

export const getFieldRule = field => toName(field && field.rule, FIELD_RULE_NAMES);

const hasControlOrBidiCharacters = (value) => {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)
      || (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069) || code === 0xfeff) {
      return true;
    }
  }
  return false;
};

const isBlank = value => value === null || value === undefined || String(value).trim() === '';

const hasBadCharacters = value => hasControlOrBidiCharacters(value) || hasInvalidCharacters(value);

export const parseCrossCheckDate = value => (isBlank(value) ? null : parse(value, CROSS_CHECK_DATE_FORMAT, new Date()));

export const validateCustomerId = (customerId) => {
  const value = customerId === null || customerId === undefined ? '' : String(customerId);
  if (value.trim() === '') {
    return 'Customer ID is required.';
  }
  if (value.length > CUSTOMER_ID_MAX_LENGTH) {
    return `Customer ID must not exceed ${ CUSTOMER_ID_MAX_LENGTH } characters.`;
  }
  if (hasBadCharacters(value)) {
    return 'Customer ID contains invalid characters.';
  }
  return null;
};

const validateNamePart = (label, value, maxLength) => {
  if (isBlank(value)) {
    return null;
  }
  if (String(value).length > maxLength) {
    return `${ label } must not exceed ${ maxLength } characters.`;
  }
  if (hasBadCharacters(String(value))) {
    return `${ label } contains invalid characters.`;
  }
  return null;
};

export const validateCustomerName = ({ customerType, useFullName, customerName, firstName, middleName, lastName }) => {
  const errors = [];
  const fullNameMode = customerType === 'entity' || useFullName !== false;

  if (fullNameMode) {
    if (isBlank(customerName)) {
      errors.push('Customer Name is required.');
    } else {
      errors.push(validateNamePart('Customer Name', customerName, CUSTOMER_NAME_MAX_LENGTH));
    }
  } else {
    if (isBlank(firstName)) {
      errors.push('First Name is required.');
    }
    if (isBlank(lastName)) {
      errors.push('Last Name is required.');
    }
    errors.push(validateNamePart('First Name', firstName, NAME_PART_MAX_LENGTH));
    errors.push(validateNamePart('Middle Name', middleName, NAME_PART_MAX_LENGTH));
    errors.push(validateNamePart('Last Name', lastName, NAME_PART_MAX_LENGTH));
  }

  const messages = errors.filter(error => error);
  return { valid: messages.length === 0, messages };
};

export const validateCrossCheckValue = (field, rawValue) => {
  const value = isBlank(rawValue) ? '' : String(rawValue).trim();
  if (value === '') {
    return field.mandatory ? `${ field.label } is required.` : null;
  }

  const maxLength = field.maxLength > 0 ? field.maxLength : DEFAULT_FIELD_MAX_LENGTH;
  if (value.length > maxLength) {
    return `${ field.label } must not exceed ${ maxLength } characters.`;
  }
  if (hasBadCharacters(value)) {
    return `${ field.label } contains invalid characters.`;
  }

  switch (getFieldRule(field)) {
    case 'alphanumeric':
      return ALPHANUMERIC_REGEX.test(value) ? null : `${ field.label } may only contain letters and digits.`;
    case 'ustin':
      return US_TIN_REGEX.test(value) ? null : `${ field.label } must be exactly 9 digits.`;
    case 'phonenumber':
      return PHONE_REGEX.test(value) ? null : `${ field.label } may only contain digits, spaces, +, - and parentheses (max 30 characters).`;
    case 'dateofbirth': {
      const date = parseCrossCheckDate(value);
      if (!isValid(date)) {
        return `${ field.label } is not a valid date.`;
      }
      if (isAfter(date, startOfDay(new Date()))) {
        return `${ field.label } cannot be in the future.`;
      }
      if (isBefore(date, MIN_DATE_OF_BIRTH)) {
        return `${ field.label } must be on or after 1900-01-01.`;
      }
      return null;
    }
    default:
      return null;
  }
};

export const validateCrossCheck = (fields, values) => {
  const errors = {};
  (fields || []).forEach((field) => {
    const message = validateCrossCheckValue(field, values ? values[field.key] : null);
    if (message) {
      errors[field.key] = message;
    }
  });
  return errors;
};

export const buildCrossCheckPayload = (fields, values) => {
  const payload = {};
  (fields || []).forEach((field) => {
    const raw = values ? values[field.key] : null;
    if (isBlank(raw)) {
      return;
    }
    const value = String(raw).trim();
    payload[field.key] = getFieldType(field) === 'date' ? format(parseCrossCheckDate(value), 'yyyy-MM-dd') : value;
  });
  return payload;
};
